import { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import useLoanManagement from "../../../../hooks/useLoanManagement";
import { LoanManagement } from "../../../../services/loanManagement";
import { LoanStatus } from "../../../../constant/enum";
import { toast } from "react-toastify";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_SortingState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import {
  formatNumbersWithThousandSeperators,
  formatPercentageFromRatio,
} from "@funded-here-interface/common/src/Utils/formatter";

const LtvSf: FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const [data, setData] = useState<LoanManagement[]>([]);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [totalRowCount, setTotalRowCount] = useState(0);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const {
    data: loanData,
    isLoading,
    error,
  } = useLoanManagement(token, LoanStatus.Ltv, pagination, [], sorting);
  useEffect(() => {
    if (loanData) {
      setData(loanData.data);
      setTotalRowCount(loanData.meta.totalRowCount);
    }
    if (error) {
      toast.error((error as Error).message);
    }
  }, [loanData, isLoading, error]);

  const columns = useMemo<MRT_ColumnDef<LoanManagement>[]>(
    () => [
      {
        accessorKey: "skuId",
        header: "SKU ID",
      },
      {
        accessorKey: "orgName",
        header: "Org Name",
      },
      {
        accessorKey: "distributorOrgName",
        header: "Supplier Name", // Distributor / Supplier
      },
      {
        accessorKey: "availableCreditLine",
        header: "Available Credit Line",
        Cell: ({ row }) => {
          const currency = row?.original?.loanApplicationCurrency || "";
          const creditLine = formatNumbersWithThousandSeperators(
            parseFloat(row?.original?.availableCreditLine || "0")
          );
          return <p>{`${currency} ${creditLine}`}</p>;
        },
      },
      {
        accessorKey: "invoiceValue",
        header: "Invoice Value",
        Cell: ({ row }) => {
          const currency = row?.original?.loanApplicationCurrency || "";
          const creditLine = formatNumbersWithThousandSeperators(
            parseFloat(row?.original?.invoiceValue || "0")
          );
          return <p>{`${currency} ${creditLine}`}</p>;
        },
      },
      {
        accessorKey: "status",
        header: "Status",
      },
      {
        accessorKey: "loanApplicationRejectionReason",
        header: "Application Rejection Reason",
        Cell: ({ cell }) => <p>{cell.getValue<string>() || "-"}</p>,
      },
      {
        accessorKey: "loanToValuePercentage",
        header: "LTV %",
        Cell: ({ cell }) => (
          <p>{formatPercentageFromRatio(cell.getValue<string>()) + "%"}</p>
        ),
      },
      {
        accessorKey: "loanAmount2",
        header: "LTV Amount",
        Cell: ({ row }) => {
          const currency = row?.original?.loanApplicationCurrency || "";
          const creditLine = formatNumbersWithThousandSeperators(
            parseFloat(row?.original?.loanAmount2 || "0")
          );
          return <p>{`${currency} ${creditLine}`}</p>;
        },
      },
      {
        accessorKey: "interestRate",
        header: "Interest Rate",
        Cell: ({ cell }) => (
          <p>{formatPercentageFromRatio(cell.getValue<string>()) + "%"}</p>
        ),
      },
      {
        accessorKey: "tenure",
        header: "Tenure",
        Cell: ({ cell }) => <p>{cell.getValue<number>() || "-"}</p>,
      },
      {
        accessorKey: "moe",
        header: "QC",
        Cell: ({ row }) => {
          const currency = row?.original?.loanApplicationCurrency || "";
          const creditLine = formatNumbersWithThousandSeperators(
            parseFloat(row?.original?.moe || "0")
          );
          return <p>{`${currency} ${creditLine}`}</p>;
        },
      },
      {
        accessorKey: "sinkingFund",
        header: "SF",
        Cell: ({ row }) => {
          const currency = row?.original?.loanApplicationCurrency || "";
          const creditLine = formatNumbersWithThousandSeperators(
            parseFloat(row?.original?.sinkingFund || "0")
          );
          return <p>{`${currency} ${creditLine}`}</p>;
        },
      },
      {
        accessorKey: "additionalSinkingFund",
        header: "ASF",
        Cell: ({ row }) => {
          const currency = row?.original?.loanApplicationCurrency || "";
          const creditLine = formatNumbersWithThousandSeperators(
            parseFloat(row?.original?.additionalSinkingFund || "0")
          );
          return <p>{`${currency} ${creditLine}`}</p>;
        },
      },
    ],
    []
  );

  const table = useMantineReactTable({
    enableColumnActions: false,
    enableColumnFilters: false,
    paginationDisplayMode: "pages",
    manualPagination: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: { pagination, isLoading, sorting },
    rowCount: totalRowCount,
    columns,
    data: data,
    initialState: { density: "xs" },
    mantineTableProps: {
      withBorder: true,
    },
    mantineTableHeadCellProps: {
      style: {
        userSelect: "text",
      },
    },
  });

  return <MantineReactTable table={table} />;
};

export default LtvSf;
